import { Stack, Typography } from "@mui/material"
import { motion, MotionProps } from "framer-motion"
import React from "react"

const LOADING_MESSAGES = [
  "Retrieving data from Arweave’s permaweb—decentralized, permanent, and censorship-free. No servers, no gatekeepers, just pure onchain access.",
  "Your request flows through AO Processes—autonomous, unstoppable, and fully decentralized. Arweave ensures the data is permanent. No shortcuts, no compromises.",
  "Loading… No centralized servers are involved. Just AO Processes running trustlessly and Arweave storing data forever.",
  "Summoning truth from the permaweb—Arweave ensures data permanence, and AO executes autonomously. This isn’t just loading; it’s decentralization in action.",
  "Your request is handled by autonomous AO agents—unstoppable and decentralized—while Arweave keeps the data immutable and forever accessible.",
  "Decentralization isn’t just a word—your data is stored on Arweave, executed by AO, and verified onchain. No middlemen, no backdoors.",
  "Unlike centralized apps, this is fully on-chain. Arweave stores, AO executes, and no single entity can take it down. Just pure decentralization loading...",
  "Fetching from the permaweb… No databases, no corporate servers—Arweave and AO keep your data and execution fully onchain.",
  "Your request is weaving through Arweave’s permanent storage and AO’s unstoppable processes. A little patience for true decentralization.",
  "Loading data from the blockchain’s memory—Arweave keeps it stored forever, AO keeps it running. No centralized infrastructure, just trustless execution.",
  "Hold tight—your request is going through Arweave’s immutable ledger and AO’s autonomous agents. This is on-chain at its finest.",
  "No cloud servers, no shutdown risks. Arweave ensures the data is always there, AO ensures the logic runs without permission. True decentralization is worth the wait.",
  "Processing via AO, retrieving from Arweave—every step is decentralized, censorship-resistant, and unstoppable. Just how it should be.",
  "The future doesn’t load instantly—but it does last forever. Arweave holds your data, AO runs your logic, all onchain, always accessible.",
  "Decentralization isn’t about speed—it’s about permanence and resilience. Arweave stores, AO executes, and your request is loading in trustless autonomy.",
]

function LoadingSpinnerBase() {
  return (
    <Stack
      sx={{ minHeight: 150, paddingX: 2, paddingY: 4 }}
      justifyContent="center"
      alignItems="center"
      gap={3}
    >
      {/* <CircularProgress size={32} color="secondary" /> */}
      <Dexi animate="show" />
      <Typography maxWidth={420} variant="caption" align="center" color="text.secondary">
        {LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]}
      </Typography>
    </Stack>
  )
}

export const LoadingSpinner = React.memo(LoadingSpinnerBase)

type Props = {
  width?: number
  height?: number
  viewBox?: string
} & MotionProps

export function Dexi(props: Props) {
  const { width = 40, height = 40, viewBox = "0 0 63 63", ...rest } = props

  return (
    <motion.svg
      width={width}
      height={height}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
      variants={{
        rest: { scale: 0.5, opacity: 0 },
        show: {
          scaleX: [1, 0.66, 1],
          y: [0, -15, 0],
          transition: {
            duration: 0.75,
            ease: [0.34, 1.56, 0.64, 1],
            repeat: Infinity,
            repeatDelay: 1,
          },
        },
      }}
      style={{
        marginTop: 20,
      }}
    >
      <ellipse
        cx="31.9085"
        cy="38.4381"
        rx="30.2796"
        ry="23.942"
        fill="var(--mui-palette-background-default)"
      />
      <path
        d="M25.8343 14.5784C27.6159 14.3024 29.4623 14.1574 31.3595 14.1574C33.1353 14.1574 34.8665 14.2845 36.5419 14.5271C36.3324 13.6223 35.8794 12.8053 35.2522 12.1426C33.9769 12.013 32.6778 11.9463 31.3595 11.9463C29.9142 11.9463 28.4919 12.0265 27.0989 12.1818C26.479 12.85 26.0344 13.671 25.8343 14.5784Z"
        fill="var(--mui-palette-text-primary)"
      />
      <path
        d="M24.0758 12.6442C23.7321 13.3859 23.511 14.1913 23.4368 15.0368C11.176 17.8359 2.52728 26.9652 2.52728 37.3186C2.52728 49.7869 15.0702 60.4798 31.3595 60.4798C47.6488 60.4798 60.1916 49.7869 60.1916 37.3186C60.1916 26.8703 51.3836 17.6686 38.9439 14.9614C38.861 14.1172 38.6313 13.3138 38.2796 12.5751C52.1464 15.1391 62.4982 25.2438 62.4982 37.3186C62.4982 51.3314 48.5569 62.6909 31.3595 62.6909C14.162 62.6909 0.220703 51.3314 0.220703 37.3186C0.220703 25.3496 10.3919 15.3163 24.0758 12.6442Z"
        fill="var(--mui-palette-text-primary)"
      />
      <path
        d="M52.3898 0.682983C54.0812 -0.175038 56.1704 1.13913 55.919 2.846C55.5446 5.38755 55.1481 8.85928 55.0354 12.6882C54.9746 14.7542 55.6117 16.7304 56.4354 18.3972C57.2956 20.1378 55.6158 22.5507 53.5042 22.1828C47.6422 21.1613 43.14 19.9569 38.2123 18.2304C38.7035 17.3304 38.9787 16.3213 38.9787 15.2555C38.9787 13.3586 38.1069 11.6414 36.6975 10.3985C41.6161 6.66143 46.1069 3.87023 52.3898 0.682983Z"
        fill="#EBB9CE"
      />
      <path
        d="M24.3755 11.9392C18.0587 8.79764 12.5425 7.07694 5.00901 5.06636C3.26171 4.60003 1.63042 6.0349 2.14421 7.58024L4.36792 14.2685C4.93951 15.9877 5.13832 17.7865 4.95392 19.5707L4.37496 25.1724C4.21665 26.7042 5.97088 27.8301 7.56878 27.2146C14.7188 24.4605 19.6895 22.2339 24.7803 19.1488C23.9156 18.0424 23.4093 16.7011 23.4093 15.2555C23.4093 14.0528 23.7597 12.9224 24.3755 11.9392Z"
        fill="#EBB9CE"
      />
      <path
        d="M36.6721 15.2555C36.6721 17.9245 34.2194 20.0882 31.1939 20.0882C28.1685 20.0882 25.7159 17.9245 25.7159 15.2555C25.7159 12.5865 28.1685 10.4228 31.1939 10.4228C34.2194 10.4228 36.6721 12.5865 36.6721 15.2555Z"
        fill="#EBB9CE"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.3041 32.7051C21.0322 32.7051 21.6224 33.2491 21.6224 33.9201V36.1254C21.6224 36.7965 21.0322 37.3404 20.3041 37.3404C19.5761 37.3404 18.9859 36.7965 18.9859 36.1254V33.9201C18.9859 33.2491 19.5761 32.7051 20.3041 32.7051ZM26.2929 33.2265C26.9042 33.5909 27.0793 34.3431 26.6839 34.9066L25.3657 36.7852C24.9703 37.3487 24.1543 37.5101 23.5429 37.1457C22.9316 36.7812 22.7565 36.0291 23.1519 35.4656L24.4701 33.5869C24.8655 33.0234 25.6816 32.8621 26.2929 33.2265ZM14.3136 33.2276C14.9243 32.8622 15.7407 33.0224 16.137 33.5853L17.6181 35.6886C18.0145 36.2515 17.8407 37.004 17.23 37.3693C16.6193 37.7346 15.803 37.5745 15.4066 37.0116L13.9255 34.9082C13.5292 34.3453 13.7029 33.5929 14.3136 33.2276Z"
        fill="var(--mui-palette-text-primary)"
      />
      <path
        d="M11.7537 37.8651C11.7612 39.085 13.0861 39.9142 14.3434 39.53C16.2949 38.9336 18.0506 38.5425 19.8283 38.4089C22.2027 38.2305 24.6164 38.5116 27.5882 39.3766C28.865 39.7483 30.2139 38.8967 30.2062 37.6617C30.2018 36.9416 29.7178 36.2947 28.9799 36.0597C22.9065 34.1254 19.0393 34.3978 13.0373 36.2081C12.2632 36.4416 11.7491 37.115 11.7537 37.8651Z"
        fill="var(--mui-palette-text-primary)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M46.3551 32.7051C47.0831 32.7014 47.6766 33.2423 47.6807 33.9133L47.694 36.1186C47.698 36.7896 47.1112 37.3367 46.3831 37.3404C45.6551 37.3441 45.0616 36.8032 45.0576 36.1322L45.0443 33.9269C45.0402 33.2558 45.6271 32.7088 46.3551 32.7051ZM52.3469 33.1957C52.9604 33.557 53.1401 34.3083 52.7481 34.8738L51.4413 36.7592C51.0493 37.3247 50.2342 37.4902 49.6207 37.129C49.0072 36.7677 48.8276 36.0164 49.2195 35.451L50.5264 33.5655C50.9183 33 51.7334 32.8345 52.3469 33.1957ZM40.3678 33.2583C40.9763 32.8899 41.7936 33.0458 42.1934 33.6067L43.6872 35.7024C44.0869 36.2633 43.9177 37.0166 43.3093 37.385C42.7008 37.7535 41.8835 37.5975 41.4837 37.0367L39.9899 34.941C39.5902 34.3801 39.7594 33.6268 40.3678 33.2583Z"
        fill="var(--mui-palette-text-primary)"
      />
      <path
        d="M37.126 37.8651C37.1336 39.085 38.4584 39.9142 39.7157 39.53C41.6672 38.9336 43.4229 38.5425 45.2006 38.4089C47.575 38.2305 49.9887 38.5116 52.9606 39.3766C54.2373 39.7483 55.5862 38.8967 55.5785 37.6617C55.5741 36.9416 55.0901 36.2947 54.3522 36.0597C48.2788 34.1254 44.4116 34.3978 38.4096 36.2081C37.6356 36.4416 37.1214 37.115 37.126 37.8651Z"
        fill="var(--mui-palette-text-primary)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M39.7157 39.53C38.4584 39.9142 37.1336 39.085 37.126 37.8651C37.1214 37.115 37.6356 36.4416 38.4096 36.2081C44.4116 34.3978 48.2788 34.1254 54.3522 36.0597C55.0901 36.2947 55.5741 36.9416 55.5785 37.6617C55.5862 38.8967 54.2373 39.7483 52.9606 39.3766C49.9887 38.5116 47.575 38.2305 45.2006 38.4089C43.4229 38.5425 41.6672 38.9336 39.7157 39.53Z"
        fill="var(--mui-palette-text-primary)"
      />
    </motion.svg>
  )
}
